/**
 * CLEANROOM LAYOUT CALCULATOR LOGIC
 */

export const CALC_CONSTANTS = {
    CFM_PER_HEPA: 600,
    CFM_PER_RETURN: 500,
    LIGHTING_BASE_SQFT: 100, // 1 fixture per 100 sqft
    LIGHTING_BASE_SQM: 9.3,   // 1 fixture per ~9.3 sqm
    M3_TO_FT3: 35.3147,
    M_TO_FT: 3.28084,
    MIN_HEIGHT_FT: 7,
    MIN_HEIGHT_M: 2.13,
};

export const ACH_RANGES = {
    'ISO 8': { min: 10, max: 30 },
    'ISO 7': { min: 30, max: 65 },
    'ISO 6': { min: 80, max: 150 },
    'ISO 5': { min: 200, max: 450 },
    'USP 797/800 (ISO 7)': { min: 30, max: 30 },
    'USP 797/800 (ISO 8)': { min: 20, max: 20 },
    'cGMP Grade A': { min: 240, max: 480 },
    'cGMP Grade B': { min: 40, max: 60 },
    'cGMP Grade C': { min: 20, max: 40 },
    'cGMP Grade D': { min: 10, max: 20 },
};

export const LIGHTING_FACTORS = {
    'regular': 1.0,
    'high': 1.5,
    'very_high': 2.0,
};

function resolveAchFromRange(range, achTarget) {
    if (achTarget === 'max' || achTarget === 'maximum') {
        return range.max;
    }

    if (achTarget === 'mid' || achTarget === 'balanced') {
        return Math.round((range.min + range.max) / 2);
    }

    return range.min;
}

export function getComplianceStatus(roomType, ach) {
    const normalizedRoomType = roomType || 'ISO 8';
    const numericAch = parseFloat(ach) || 0;
    const range = ACH_RANGES[normalizedRoomType];

    if (!range) {
        return {
            status: 'unknown',
            min: null,
            max: null,
            message: 'No compliance range is defined for this standard.'
        };
    }

    if (numericAch < range.min) {
        return {
            status: 'below_min',
            min: range.min,
            max: range.max,
            message: `Below minimum ACH (${range.min}) for ${normalizedRoomType}.`
        };
    }

    if (numericAch > range.max) {
        return {
            status: 'above_recommended',
            min: range.min,
            max: range.max,
            message: `Above typical ACH range (${range.min}-${range.max}) for ${normalizedRoomType}.`
        };
    }

    return {
        status: 'compliant',
        min: range.min,
        max: range.max,
        message: `Compliant with ${normalizedRoomType} ACH range (${range.min}-${range.max}).`
    };
}

export function getRecommendation(room, results) {
    const roomType = room.roomType || 'ISO 8';
    const compliance = getComplianceStatus(roomType, results.ach);
    const hasFixedAch = compliance.min !== null && compliance.max !== null && compliance.min === compliance.max;

    if (compliance.status === 'below_min') {
        return `Increase ACH to at least ${compliance.min} to meet ${roomType} compliance.`;
    }

    if (compliance.status === 'above_recommended') {
        return `ACH is above the typical ${roomType} range. Confirm airflow and energy targets with engineering.`;
    }

    if (compliance.status === 'compliant') {
        if (hasFixedAch) {
            return `Maintain ACH at ${compliance.min} for sustained ${roomType} compliance.`;
        }

        return `Maintain ACH between ${compliance.min} and ${compliance.max} for sustained ${roomType} compliance.`;
    }

    return 'Review room standard selection to validate compliance targets.';
}

/**
 * Calculates HVAC requirements for a single room.
 * 
 * @param {Object} room Room object with length, width, height, roomType, etc.
 * @param {string} units 'imperial' or 'metric'
 * @returns {Object|null} Results object or null if dimensions are invalid.
 */
export function calculateRoom(room, units = 'imperial') {
    const L = parseFloat(room.length) || 0;
    const W = parseFloat(room.width) || 0;
    const H = parseFloat(room.height) || 0;
    const hasRequiredSelections = Boolean(room.roomType && room.achTarget && room.lighting);

    // Only calculate if dimensions are valid positive numbers
    if (L <= 0 || W <= 0 || H <= 0 || !hasRequiredSelections) {
        return null;
    }

    const isMetric = units === 'metric';
    const minHeight = isMetric ? CALC_CONSTANTS.MIN_HEIGHT_M : CALC_CONSTANTS.MIN_HEIGHT_FT;

    if (H < minHeight) {
        return null;
    }

    // Volume calculation
    const volume = L * W * H;
    const area = L * W;

    // Convert to imperial for core calculation if metric
    const volumeFt3 = isMetric ? volume * CALC_CONSTANTS.M3_TO_FT3 : volume;
    const areaSqFt = isMetric ? area * (CALC_CONSTANTS.M_TO_FT ** 2) : area;

    // Airflow Calculation Logic (CFM-only, derived from selected standard)
    const range = ACH_RANGES[room.roomType] || ACH_RANGES['ISO 8'];
    const achTarget = room.achTarget || 'balanced';
    const ach = resolveAchFromRange(range, achTarget);
    const cfm = Math.ceil((volumeFt3 * ach) / 60);

    const hepaCount = Math.ceil(cfm / CALC_CONSTANTS.CFM_PER_HEPA);
    const returnCount = Math.ceil(cfm / CALC_CONSTANTS.CFM_PER_RETURN);

    // Lighting
    const lightingFactor = LIGHTING_FACTORS[room.lighting] || 1.0;
    const baseSqFt = CALC_CONSTANTS.LIGHTING_BASE_SQFT || 100;
    const fixtures = Math.ceil((areaSqFt / baseSqFt) * lightingFactor);

    // Achievable ACH based on the actual HEPA units calculated
    // (hepaCount * CFM_per_HEPA * 60 min) / volume_ft3
    const achievableAch = Math.round((hepaCount * CALC_CONSTANTS.CFM_PER_HEPA * 60) / volumeFt3);
    const compliance = getComplianceStatus(room.roomType, achievableAch);
    const recommendation = getRecommendation(room, { ach: achievableAch });

    return {
        volume,
        area,
        ach: achievableAch,
        cfm,
        hepaCount,
        returnCount,
        fixtures,
        isMetric,
        complianceStatus: compliance.status,
        complianceLabel: compliance.message,
        complianceMin: compliance.min,
        complianceMax: compliance.max,
        recommendedAction: recommendation
    };
}

/**
 * Calculates totals across all rooms.
 * 
 * @param {Array} rooms Array of room objects.
 * @param {string} units 'imperial' or 'metric'
 * @returns {Object} Totals object.
 */
export function calculateTotals(rooms, units = 'imperial') {
    return rooms.reduce((acc, room) => {
        const results = calculateRoom(room, units);
        if (!results) return acc;

        return {
            totalCfm: acc.totalCfm + results.cfm,
            totalHepa: acc.totalHepa + results.hepaCount,
            totalReturns: acc.totalReturns + results.returnCount,
            totalFixtures: acc.totalFixtures + results.fixtures,
        };
    }, { totalCfm: 0, totalHepa: 0, totalReturns: 0, totalFixtures: 0 });
}
